import React from 'react';
import PhoneIcon from '@material-ui/icons/Phone';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {makeStyles} from '@material-ui/core/styles';

const PHONE_NUMBER = '0482099121';
const PHONE_DISPLAY = '0482 099 121';

function isTouchDevice() {
  return navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'fixed',
    bottom: '1.5rem',
    right: '1.5rem',
    zIndex: 99999,
    [theme.breakpoints.down('sm')]: {
      right: '50%',
      transform: 'translateX(50%)',
    },
  },
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px 10px 10px',
    borderRadius: '40px',
    backgroundColor: theme.palette.secondary.dark,
    color: theme.palette.common.white,
    textDecoration: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    transition: 'transform 150ms ease, box-shadow 150ms ease',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.25)',
    '&:hover': {
      color: theme.palette.common.white,
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
    },
    '&:active': {
      transform: 'scale(0.97)',
      boxShadow: '0 1px 6px rgba(0, 0, 0, 0.2)',
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.secondary.main}`,
      outlineOffset: '3px',
    },
  },
  iconCircle: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textStack: {
    display: 'flex',
    flexDirection: 'column',
    '& > :first-child': {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      opacity: 0.85,
    },
    '& > :last-child': {
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
  },
  copyIcon: {
    marginLeft: '2px',
    opacity: 0.6,
    fontSize: '18px !important',
    transition: 'opacity 150ms ease',
    '$root:hover &': {
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

const PhoneHelpBar = () => {
  const classes = useStyles();
  const isProvider = useSelector(state => state.auth.isProvider);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const handleClick = async (e) => {
    if (isTouchDevice() && isMobile) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(PHONE_NUMBER);
      toast.success('Phone number copied!');
    } catch {
      toast.error('Failed to copy phone number');
    }
  };

  if (isProvider) return null;

  return (
    <div className={classes.wrapper}>
      <a
        href={`tel:${PHONE_NUMBER}`}
        onClick={handleClick}
        className={classes.root}
        aria-label={`Get help finding support. Call ${PHONE_DISPLAY}`}
      >
        <span className={classes.iconCircle}><PhoneIcon fontSize='small' /></span>
        <span className={classes.textStack}>
          <span>Get Help Finding Support</span>
          <span>{PHONE_DISPLAY}</span>
        </span>
        <FileCopyOutlinedIcon className={classes.copyIcon} titleAccess='Copy phone number' />
      </a>
    </div>
  );
};

export default PhoneHelpBar;
